/**
 * TREATX-CANCELLATION-SSOT-CONSOLIDATION-01
 *
 * Single presentation component for the frozen cancellation/refund policy
 * snapshot on quote surfaces (patient quote view, patient offer modal,
 * provider/sales offer card).
 *
 * Rules (Owner charter):
 *  - The snapshot BODY is the FULL legal document; it is NEVER dumped
 *    inline into these compact surfaces and never rendered as raw markup.
 *  - Show the localized SUMMARY only when the issuing version carried one
 *    for THIS locale — no cross-locale fallback, no generated wording.
 *  - Always expose the canonical document title + version/captured-at
 *    metadata and a deliberate action to open the full document.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FileText } from 'lucide-react';

export const CANCELLATION_POLICY_KEY = 'cancellation_and_refund_conditions';

const localeOf = (language) => String(language || 'en').slice(0, 2).toLowerCase();

export const CancellationPolicyRef = ({ snapshot, language, testId = 'cancellation-policy-ref' }) => {
  const { t } = useTranslation();

  if (!snapshot || typeof snapshot !== 'object') {
    return (
      <p className="text-xs text-gray-500 italic" data-testid={`${testId}-not-captured`}>
        {t('patient.offers.policyNotCaptured',
          'Cancellation policy was not captured at issuance for this offer. Refer to the current provider policy for guidance.')}
      </p>
    );
  }

  const locale = localeOf(language);
  const summaryMap = snapshot.summary || {};
  const titleMap = snapshot.title || {};
  const summary = typeof summaryMap[locale] === 'string' && summaryMap[locale].trim()
    ? summaryMap[locale].trim()
    : null;
  const title = titleMap[locale] || titleMap.en
    || t('legal.cancellationConditions.title', 'Cancellation & Refund Conditions');
  const contentKey = snapshot.content_key || CANCELLATION_POLICY_KEY;

  return (
    <div className="space-y-1" data-testid={testId}>
      {summary && (
        <p className="text-sm text-gray-700" data-testid={`${testId}-summary`}>{summary}</p>
      )}
      <a
        href={`/legal/${contentKey}`}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-700 hover:text-blue-900 hover:underline transition-colors"
        data-testid={`${testId}-view-full`}
      >
        <FileText className="h-3.5 w-3.5" />
        {t('legal.cancellationConditions.viewFull', 'View Cancellation & Refund Conditions')}
      </a>
      <p className="text-[11px] text-gray-400 italic" data-testid={`${testId}-meta`}>
        {title}
        {snapshot.version != null ? ` · v${snapshot.version}` : ''}
        {snapshot.captured_at
          ? ` · ${t('patient.offers.capturedAtIssuance', 'captured at issuance')}: ${String(snapshot.captured_at).slice(0, 10)}`
          : ''}
      </p>
    </div>
  );
};

export default CancellationPolicyRef;
